// iter411 — Smart Reconcile daily digest widget.
// User asked for a "daily push so I don't have to remember to re-match".
// Renders a compact card showing the snapshot the cron would post:
//   • auto_resolved_24h     — txns the matcher resolved in last 24h
//   • pt_suggestions_open   — pending pass-through advance suggestions
//   • unassigned_total      — backlog of txns still needing manual review
// Click into the queue to deal with it, or "Run digest now" to force-fire.
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, ArrowRight, CheckCircle2, AlertCircle, ClipboardList, Send, Flag, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { StaleRulePromoteDialog } from './StaleRulePromoteDialog';

export function SmartReconcileWidget({ testidPrefix = 'smart-recon' }) {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  // iter414 — Bulk auto-promote stale rules dialog state.
  const [promoteOpen, setPromoteOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/scheduler/smart-reconcile/preview');
      setSnap(r.data || null);
    } catch { setSnap(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    if (running) return;
    setRunning(true);
    try {
      await api.post('/scheduler/smart-reconcile/run-now');
      toast.success('Smart Reconcile digest fired — check the bell icon');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
    } finally { setRunning(false); }
  };

  if (loading) {
    return (
      <Card data-testid={`${testidPrefix}-card`} className="border-emerald-200/60">
        <CardContent className="py-4 px-4 text-xs text-muted-foreground">Loading Smart Reconcile…</CardContent>
      </Card>
    );
  }
  if (!snap) return null;

  const actionable = (snap.pt_suggestions_open || 0) + (snap.new_unassigned_24h || 0) + (snap.needs_attention || 0);
  const calm = actionable === 0;

  return (
    <Card data-testid={`${testidPrefix}-card`} className="border-emerald-200/60 bg-gradient-to-br from-emerald-50/40 to-white">
      <CardHeader className="pb-2 pt-3 px-4 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
          <Sparkles size={13} className="text-emerald-600" />
          Smart Reconcile · last 24h
        </CardTitle>
        <div className="flex items-center gap-1">
          {/* iter414 — Bulk auto-promote button. Only shown when there are
              stale items to clean up. Opens the StaleRulePromoteDialog. */}
          {(snap.needs_attention || 0) >= 3 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[10px] px-2 text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100"
              onClick={() => setPromoteOpen(true)}
              data-testid={`${testidPrefix}-promote-stale`}
              title="Cluster stale txns by description and bulk-create auto-rules"
            >
              <Zap size={10} className="mr-1" />
              Auto-promote
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-[10px] px-2 text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100"
            onClick={runNow}
            disabled={running}
            data-testid={`${testidPrefix}-run-now`}
            title="Force-run the digest now and post a fresh notification"
          >
            <Send size={10} className="mr-1" />
            {running ? 'Running…' : 'Run digest'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-1 space-y-2">
        {calm ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-1" data-testid={`${testidPrefix}-empty`}>
            <CheckCircle2 size={12} className="text-emerald-600" />
            All clear — nothing pending review since last digest.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center" data-testid={`${testidPrefix}-stats`}>
            <Stat label="Auto-resolved" value={snap.auto_resolved_24h} icon={<CheckCircle2 size={11} className="text-emerald-600" />} testid={`${testidPrefix}-resolved`} />
            <Stat label="Suggestions" value={snap.pt_suggestions_open} icon={<ClipboardList size={11} className="text-violet-600" />} accent={snap.pt_suggestions_open > 0 ? 'text-violet-700' : ''} testid={`${testidPrefix}-suggestions`} />
            <Stat label="Unassigned" value={snap.unassigned_total} icon={<AlertCircle size={11} className="text-amber-600" />} accent={snap.unassigned_total > 0 ? 'text-amber-700' : ''} testid={`${testidPrefix}-unassigned`} />
            {/* iter412 — Stale "needs attention" red-flag stat. Surfaces
                txns that have been unassigned for ≥7 days. Click jumps
                to the Quick-Link inbox with the `?stale=1` filter. */}
            <Link to="/banking-quick-link?stale=1" className="block" data-testid={`${testidPrefix}-needs-attention-link`}>
              <Stat
                label="Needs review"
                value={snap.needs_attention}
                icon={<Flag size={11} className={snap.needs_attention > 0 ? 'text-rose-600' : 'text-muted-foreground'} />}
                accent={snap.needs_attention > 0 ? 'text-rose-700' : ''}
                testid={`${testidPrefix}-needs-attention`}
              />
            </Link>
          </div>
        )}
        {!calm && (
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <Button asChild variant="outline" size="sm" className="h-7 text-[11px] px-2.5 ml-auto" data-testid={`${testidPrefix}-cta`}>
              <Link to="/banking-quick-link">
                Open Quick-Link <ArrowRight size={11} className="ml-1" />
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
      {/* iter414 — bulk auto-promote dialog. Refresh snapshot on close. */}
      <StaleRulePromoteDialog
        open={promoteOpen}
        onOpenChange={setPromoteOpen}
        onCreated={load}
      />
    </Card>
  );
}

function Stat({ label, value, icon, accent = '', testid }) {
  return (
    <div className="rounded-md border border-border/60 bg-white px-2 py-1.5" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
        {icon}{label}
      </div>
      <div className={`text-base font-bold tabular-nums leading-tight ${accent}`}>{value || 0}</div>
    </div>
  );
}

export default SmartReconcileWidget;
